/**
 * MHR Breadcrumb Navigation Component
 *
 * Provides hierarchical navigation breadcrumbs for MHR pages.
 * Improves context awareness and navigation within the module.
 */

import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href?: string; // If no href, it's the current page (not clickable)
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  /** Optional: Show home icon for first item */
  showHomeIcon?: boolean;
}

export function Breadcrumb({ items, showHomeIcon = true }: BreadcrumbProps) {
  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4 overflow-x-auto"
    >
      <ol className="flex items-center flex-wrap gap-1">
        {items.map((item, index) => {
          const isFirst = index === 0;
          const isLast = index === items.length - 1;
          const isClickable = !!item.href && !isLast;

          return (
            <li key={index} className="flex items-center min-w-0">
              {/* Separator (except for first item) */}
              {!isFirst && (
                <ChevronRight
                  size={14}
                  className="mx-1.5 text-gray-400 dark:text-gray-500 flex-shrink-0"
                  aria-hidden="true"
                />
              )}

              {/* Breadcrumb item */}
              {isClickable ? (
                <Link
                  to={item.href!}
                  className="flex items-center gap-1.5 hover:text-primary-600 dark:hover:text-primary-400 transition-colors truncate max-w-[200px]"
                  title={item.label}
                >
                  {isFirst && showHomeIcon && (
                    <Home size={14} className="flex-shrink-0" />
                  )}
                  <span className="truncate">{item.label}</span>
                </Link>
              ) : (
                <span
                  className={`flex items-center gap-1.5 truncate max-w-[200px] ${
                    isLast ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-500 dark:text-gray-400'
                  }`}
                  title={item.label}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {isFirst && showHomeIcon && (
                    <Home size={14} className="flex-shrink-0" />
                  )}
                  <span className="truncate">{item.label}</span>
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default Breadcrumb;
